/**
 * usePermissions Hook
 *
 * Provides permission checking utilities based on ABAC permissions.
 * Uses the permission matcher for wildcard-aware checks.
 */

import { useCallback, useMemo } from 'react';
import { useUser } from '@/stores';
import {
  matchPermission,
  hasModuleAccess as checkModuleAccess,
  getAccessibleModules,
  createPermission,
  normalizePermissionClaims,
  type Scope,
} from '../utils/permission-matcher';

// Role normalization helper
const normalizeRole = (role: string | undefined): string => {
  if (!role) return '';
  return role.toLowerCase().replace(/_/g, '');
};

export function usePermissions() {
  const user = useUser();

  // Get user permissions (from JWT)
  const permissions = useMemo(() => {
    return normalizePermissionClaims(user?.permissions || []);
  }, [user?.permissions]);

  // Get user roles
  const roles = useMemo(() => {
    if (!user?.role) return [];
    // Handle both single role and array
    return Array.isArray(user.role) ? user.role : [user.role];
  }, [user]);

  // Check if user is superadmin
  const isSuperAdmin = useMemo(() => {
    return roles.some((r) => normalizeRole(r) === 'superadmin');
  }, [roles]);

  // Check if user is tenant admin
  const isTenantAdmin = useMemo(() => {
    return roles.some(
      (r) => normalizeRole(r) === 'tenantadmin' || normalizeRole(r) === 'tenant_admin',
    );
  }, [roles]);

  // Check if user has wildcard permission
  const hasWildcard = useMemo(() => {
    return permissions.some((permission) => permission.includes('*'));
  }, [permissions]);

  /**
   * Check if user has a specific permission.
   * Supports ABAC format: module:resource:action:scope
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user) return false;

      return matchPermission(permissions, permission);
    },
    [user, permissions],
  );

  /**
   * Check if user has any of the specified permissions.
   */
  const hasAnyPermission = useCallback(
    (permissionList: string[]): boolean => {
      if (!user) return false;

      return permissionList.some((p) => hasPermission(p));
    },
    [user, hasPermission],
  );

  /**
   * Check if user has all of the specified permissions.
   */
  const hasAllPermissions = useCallback(
    (permissionList: string[]): boolean => {
      if (!user) return false;

      return permissionList.every((p) => hasPermission(p));
    },
    [user, hasPermission],
  );

  /**
   * Convenient action checker.
   * Usage: can('read', 'lead', 'crm') -> checks 'crm:lead:read:tenant'
   */
  const can = useCallback(
    (action: string, resource: string, module: string, scope: Scope = 'tenant'): boolean => {
      const permission = createPermission(module, resource, action, scope);
      return hasPermission(permission);
    },
    [hasPermission],
  );

  /**
   * Check if user has access to a module.
   */
  const hasModuleAccess = useCallback(
    (module: string): boolean => {
      if (!user) return false;

      return checkModuleAccess(permissions, module);
    },
    [user, permissions],
  );

  /**
   * Check if user has a specific role.
   */
  const hasRole = useCallback(
    (role: string | string[]): boolean => {
      if (!user) return false;

      const rolesToCheck = Array.isArray(role) ? role : [role];
      const normalizedUserRoles = roles.map(normalizeRole);

      return rolesToCheck.some((r) => normalizedUserRoles.includes(normalizeRole(r)));
    },
    [user, roles],
  );

  /**
   * Get list of modules the user can access.
   */
  const accessibleModules = useMemo(() => {
    if (!user) return [];
    return getAccessibleModules(permissions);
  }, [user, permissions]);

  return {
    // State
    permissions,
    roles,
    isSuperAdmin,
    isTenantAdmin,
    hasWildcard,
    accessibleModules,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    hasModuleAccess,
    hasRole,
  };
}

// Type-safe permission constants
export const Permissions = {
  // CRM (3-segment format matching ABAC role definitions)
  CRM_LEAD_READ: 'crm:leads:read',
  CRM_LEAD_CREATE: 'crm:leads:create',
  CRM_LEAD_UPDATE: 'crm:leads:update',
  CRM_LEAD_DELETE: 'crm:leads:delete',
  CRM_DEAL_READ: 'crm:deals:read',
  CRM_DEAL_CREATE: 'crm:deals:create',
  CRM_DEAL_UPDATE: 'crm:deals:update',
  CRM_DEAL_DELETE: 'crm:deals:delete',
  CRM_PIPELINE_MANAGE: 'crm:pipelines:update',
  CRM_ANALYTICS_READ: 'crm:analytics:read',

  // HRMS - Admin/Tenant Level
  HRMS_EMPLOYEE_READ: 'hrms:employee:read:tenant',
  HRMS_EMPLOYEE_CREATE: 'hrms:employee:create:tenant',
  HRMS_EMPLOYEE_UPDATE: 'hrms:employee:update:tenant',
  HRMS_EMPLOYEE_DELETE: 'hrms:employee:delete:tenant',
  HRMS_DEPARTMENT_MANAGE: 'hrms:department:manage:tenant',
  HRMS_HOLIDAY_READ: 'hrms:holiday:read:tenant',

  // HRMS - Employee Self-Service
  HRMS_EMPLOYEE_READ_SELF: 'hrms:employee:read:self',
  HRMS_EMPLOYEE_UPDATE_SELF: 'hrms:employee:update:self',
  HRMS_DOCUMENT_READ_SELF: 'hrms:document:read:self',
  HRMS_PAYSLIP_READ_SELF: 'hrms:payslip:read:self',
  HRMS_GOAL_READ_SELF: 'hrms:goal:read:self',
  HRMS_HOLIDAY_READ_SELF: 'hrms:holiday:read:self',
  HRMS_HOLIDAY_CREATE: 'hrms:holiday:create:tenant',
  HRMS_HOLIDAY_UPDATE: 'hrms:holiday:update:tenant',
  HRMS_HOLIDAY_DELETE: 'hrms:holiday:delete:tenant',
  HRMS_HOLIDAY_UPDATE_SELF: 'hrms:holiday:update:self',
  HRMS_HOLIDAY_APPROVE_TEAM: 'hrms:holiday:approve:team',
  HRMS_BENEFITS_ENROLLMENT_READ_SELF: 'hrms:benefits:enrollment:read:self',
  HRMS_BENEFITS_CLAIM_READ_SELF: 'hrms:benefits:claim:read:self',

  // Attendance - Admin/Tenant Level
  ATTENDANCE_RECORD_READ: 'attendance:record:read:tenant',
  ATTENDANCE_LEAVE_READ: 'attendance:leave_request:read:tenant',
  ATTENDANCE_LEAVE_APPROVE: 'attendance:leave_request:approve:tenant',
  ATTENDANCE_SHIFT_READ: 'attendance:shift:read:tenant',
  ATTENDANCE_TIME_EVENT_READ: 'attendance:time-event:read:tenant',
  ATTENDANCE_TIME_EVENT_RESOLVE: 'attendance:time-event:resolve:tenant',
  ATTENDANCE_BULK_READ: 'attendance:bulk:read:tenant',
  ATTENDANCE_BULK_IMPORT: 'attendance:bulk:import:tenant',
  ATTENDANCE_BULK_APPROVE: 'attendance:bulk:approve:tenant',

  // Attendance - Employee Self-Service
  ATTENDANCE_RECORD_READ_SELF: 'attendance:record:read:self',
  ATTENDANCE_RECORD_CREATE_SELF: 'attendance:record:create:self',
  ATTENDANCE_LEAVE_REQUEST_READ_SELF: 'attendance:leave_request:read:self',
  ATTENDANCE_LEAVE_REQUEST_CREATE_SELF: 'attendance:leave_request:create:self',
  ATTENDANCE_LEAVE_BALANCE_READ_SELF: 'attendance:leave_balance:read:self',
  ATTENDANCE_SHIFT_ASSIGNMENT_READ_SELF: 'attendance:shift_assignment:read:self',
  ATTENDANCE_SHIFT_SWAP_READ_SELF: 'attendance:shift-swap:read:self',
  ATTENDANCE_REGULARIZATION_CREATE_SELF: 'attendance:regularization:create:self',

  // Attendance - Violation Management
  ATTENDANCE_VIOLATION_READ_TENANT: 'attendance:violation:read:tenant',
  ATTENDANCE_VIOLATION_READ_DEPARTMENT: 'attendance:violation:read:department',
  ATTENDANCE_VIOLATION_READ_TEAM: 'attendance:violation:read:team',
  ATTENDANCE_VIOLATION_READ_SELF: 'attendance:violation:read:self',
  ATTENDANCE_VIOLATION_MANAGE_TENANT: 'attendance:violation:manage:tenant',
  ATTENDANCE_VIOLATION_MANAGE_DEPARTMENT: 'attendance:violation:manage:department',
  ATTENDANCE_VIOLATION_MANAGE_TEAM: 'attendance:violation:manage:team',

  // HRMS Leave - Multi-scope permissions
  HRMS_LEAVE_REQUEST_READ_SELF: 'hrms:leave-request:read:self',
  HRMS_LEAVE_REQUEST_READ_TEAM: 'hrms:leave-request:read:team',
  HRMS_LEAVE_REQUEST_READ_DEPARTMENT: 'hrms:leave-request:read:department',
  HRMS_LEAVE_REQUEST_READ_TENANT: 'hrms:leave-request:read:tenant',

  HRMS_LEAVE_REQUEST_APPROVE_TEAM: 'hrms:leave-request:approve:team',
  HRMS_LEAVE_REQUEST_APPROVE_DEPARTMENT: 'hrms:leave-request:approve:department',
  HRMS_LEAVE_REQUEST_APPROVE_TENANT: 'hrms:leave-request:approve:tenant',

  HRMS_LEAVE_TYPE_READ_SELF: 'hrms:leave-type:read:self',
  HRMS_LEAVE_TYPE_READ: 'hrms:leave-type:read:tenant',
  HRMS_LEAVE_TYPE_CREATE: 'hrms:leave-type:create:tenant',
  HRMS_LEAVE_TYPE_UPDATE: 'hrms:leave-type:update:tenant',
  HRMS_LEAVE_TYPE_DELETE: 'hrms:leave-type:delete:tenant',

  HRMS_LEAVE_BALANCE_READ_SELF: 'hrms:leave-balance:read:self',
  HRMS_LEAVE_BALANCE_READ_TEAM: 'hrms:leave-balance:read:team',
  HRMS_LEAVE_BALANCE_READ_DEPARTMENT: 'hrms:leave-balance:read:department',
  HRMS_LEAVE_BALANCE_READ_TENANT: 'hrms:leave-balance:read:tenant',
  HRMS_LEAVE_ADMIN_MANAGE: 'hrms:leave-admin:manage:tenant',

  HRMS_LEAVE_ENCASHMENT_CREATE_SELF: 'hrms:leave-encashment:create:self',
  HRMS_LEAVE_ENCASHMENT_APPROVE_TEAM: 'hrms:leave-encashment:approve:team',
  HRMS_LEAVE_ENCASHMENT_APPROVE_DEPARTMENT: 'hrms:leave-encashment:approve:department',
  HRMS_LEAVE_ENCASHMENT_APPROVE_TENANT: 'hrms:leave-encashment:approve:tenant',

  HRMS_APPROVAL_DELEGATION_MANAGE_SELF: 'hrms:approval-delegation:manage:self',

  // HRMS - Payroll
  HRMS_PAYROLL_RUN_CREATE: 'hrms:payroll-run:create:tenant',
  HRMS_PAYROLL_RUN_READ: 'hrms:payroll-run:read:tenant',
  HRMS_PAYROLL_RUN_SIMULATE: 'hrms:payroll-run:simulate:tenant',
  HRMS_PAYROLL_RUN_LOCK: 'hrms:payroll-run:lock:tenant',
  HRMS_PAYROLL_RUN_PROCESS: 'hrms:payroll-run:process:tenant',
  HRMS_PAYROLL_RUN_APPROVE: 'hrms:payroll-run:approve:tenant',
  HRMS_PAYROLL_RUN_DISPATCH: 'hrms:payroll-run:dispatch:tenant',
  HRMS_PAYROLL_RUN_CONFIRM_PAYMENT: 'hrms:payroll-run:confirm-payment:tenant',
  HRMS_PAYROLL_RUN_EXPORT_PAYMENTS: 'hrms:payroll-run:export-payments:tenant',
  HRMS_PAYROLL_RUN_EXPORT_ACCOUNTING: 'hrms:payroll-run:export-accounting:tenant',
  HRMS_PAYROLL_RUN_RELEASE_ACCOUNTING: 'hrms:payroll-run:release-accounting:tenant',
  HRMS_PAYROLL_ACCOUNTING_READ: 'hrms:payroll-accounting:read:tenant',
  HRMS_PAYROLL_ACCOUNTING_MANAGE: 'hrms:payroll-accounting:manage:tenant',
  HRMS_PAYSLIP_READ_TENANT: 'hrms:payslip:read:tenant',
  HRMS_PAYSLIP_DOWNLOAD_SELF: 'hrms:payslip:download:self',
  HRMS_PAYSLIP_DOWNLOAD_TENANT: 'hrms:payslip:download:tenant',
  HRMS_SALARY_CREATE: 'hrms:salary:create:tenant',
  HRMS_SALARY_READ: 'hrms:salary:read:tenant',
  HRMS_SALARY_UPDATE: 'hrms:salary:update:tenant',
  HRMS_SALARY_COMPONENT_READ: 'hrms:salary-component:read:tenant',
  HRMS_SALARY_COMPONENT_CREATE: 'hrms:salary-component:create:tenant',
  HRMS_SALARY_COMPONENT_UPDATE: 'hrms:salary-component:update:tenant',
  HRMS_STATUTORY_CONFIG_READ: 'hrms:statutory-config:read:tenant',
  HRMS_STATUTORY_CONFIG_UPDATE: 'hrms:statutory-config:update:tenant',
  HRMS_STATUTORY_PROFILE_READ: 'hrms:statutory-profile:read:tenant',
  HRMS_STATUTORY_PROFILE_UPDATE: 'hrms:statutory-profile:update:tenant',
  HRMS_SETTLEMENT_CREATE: 'hrms:settlement:create:tenant',
  HRMS_SETTLEMENT_READ: 'hrms:settlement:read:tenant',
  HRMS_SETTLEMENT_APPROVE: 'hrms:settlement:approve:tenant',
  HRMS_STATUTORY_FILES_DOWNLOAD: 'hrms:statutory-files:download:tenant',

  // HRMS - Tax Management
  HRMS_PAYROLL_TAX_READ: 'hrms:payroll-tax:read:tenant',
  HRMS_PAYROLL_TAX_READ_SELF: 'hrms:payroll-tax:read:self',
  HRMS_PAYROLL_TAX_CREATE: 'hrms:payroll-tax:create:tenant',
  HRMS_PAYROLL_TAX_CREATE_SELF: 'hrms:payroll-tax:create:self',
  HRMS_PAYROLL_TAX_UPDATE: 'hrms:payroll-tax:update:tenant',
  HRMS_PAYROLL_TAX_UPDATE_SELF: 'hrms:payroll-tax:update:self',
  HRMS_PAYROLL_TAX_DELETE: 'hrms:payroll-tax:delete:tenant',
  HRMS_PAYROLL_TAX_APPROVE: 'hrms:payroll-tax:approve:tenant',
  HRMS_PAYROLL_TAX_CALCULATE: 'hrms:payroll-tax:calculate:tenant',
  HRMS_PAYROLL_TAX_CALCULATE_SELF: 'hrms:payroll-tax:calculate:self',

  // Admin
  ADMIN_TENANT_READ: 'admin:tenant:read:global',
  ADMIN_TENANT_CREATE: 'admin:tenant:create:global',
  ADMIN_TENANT_MANAGE: 'admin:tenant:manage:global',
  ADMIN_ROLE_MANAGE: 'admin:role:manage:tenant',
  ADMIN_USER_MANAGE: 'admin:user:manage:tenant',
} as const;

export type PermissionString = (typeof Permissions)[keyof typeof Permissions];
